import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import TabContext from '@mui/lab/TabContext';
import TabList from '@mui/lab/TabList';
import { Box, Grid, IconButton, Stack } from '@mui/material';
import _ from 'lodash';
import { useEffect, useState } from 'react';
import { PanelList } from '../../../components/style/ListStyle';
import { TabText } from '../../../components/style/TabStyle';
import {
  ListContentText,
  ListSubContentText,
  ListSubTitleText,
  Title,
} from '../../../components/style/TextStyle';
import { useAuthAxios } from '../../../hooks/useAuthAxios';
import {
  InspectionScoreListLabel,
  InspectionStatusList,
  OUTDOOR_GYM_EQUIPMENT_ID,
} from '../../../utils/enum';
import { fDate } from '../../../utils/formatTime';
import { sliceSerialNumber } from '../../../utils/helper';
import { FacilityImageInput } from './CommonComponent/ImageInput';
import FacilityInfoPanel from './FacilityInfoPanel/FacilityInfoPanel';
import RegularTypeInfoPanel from './FacilityInfoPanel/RegularTypeInfoPanel';
import { AddInspectionCard } from './InspectionInfoPanel/AddInspectionForm';
import { EditInspectionCard } from './InspectionInfoPanel/EditInspectionForm';
import InstpectionInfoPanel from './InspectionInfoPanel/InspectionInfoPanel';
import { InspectionStatusChips } from './StatusChips/InspectionChip';
import { RatingStatusChips } from './StatusChips/RatingChip';
import useResponsive from '../../../hooks/useResponsive';

// ----------------------------------------------------------------------

export default function FacilityInfoCard({
  facilityId,
  onClose,
  getFacilities,
}) {
  const [value, setValue] = useState('1');
  const [mode, setMode] = useState('INFO');
  const [inspectionId, setInspectionId] = useState('');

  const [getFacilityResult, getFacility] = useAuthAxios(
    {
      method: 'GET',
      url: `/api/v1/facility/${facilityId}`,
    },
    { manual: false },
  );
  useEffect(() => {
    getFacility();
  }, [getFacility]);

  useEffect(() => {
    if (!facilityId) {
      setMode('INFO');
    }
  }, [facilityId]);

  const facility = _.get(getFacilityResult, 'response.data.result');

  const [getInspectionsResult, getInspections] = useAuthAxios(
    {
      method: 'GET',
      url: '/api/v1/inspection/facility',
      params: {
        facilityId: facilityId,
      },
    },
    { manual: false },
  );
  const inspections = _.get(getInspectionsResult, 'response.data.result');

  useEffect(() => {
    getInspections();
  }, [getInspections]);

  return (
    <PanelList
      sx={{
        display: facilityId ? 'default' : 'none',
        width: { xs: '100%', md: '480px' },
        height: { xs: 'calc(100vh - 64px)' },
        zIndex: 1000,
        paddingTop: { md: '48px' },
        // maxWidth: { xs: '100%', md: '620px' },
      }}
    >
      <IconButton
        sx={{
          mb: 2,
          position: { md: 'fixed' },
          left: { md: '950px' },
          top: { md: '102px' },
          background: { md: '#fff' },
          borderRadius: { md: '0px' },
          borderTopRightRadius: { md: '8px' },
          borderBottomRightRadius: { md: '8px' },
          '&:hover': {
            background: { md: '#fff' },
          },
        }}
        onClick={onClose}
      >
        <KeyboardArrowLeftIcon />
      </IconButton>
      {mode === 'INFO' ? (
        <InformationCard
          value={value}
          setValue={setValue}
          setMode={setMode}
          facility={facility}
          inspections={inspections}
          setInspectionId={setInspectionId}
          getResult={getInspections}
        />
      ) : mode === 'ADD' ? (
        <AddInspectionCard
          facility={facility}
          setMode={setMode}
          getInspections={getInspections}
          getFacilities={getFacilities}
          getFacility={getFacility}
        />
      ) : (
        <EditInspectionCard
          facility={facility}
          setMode={setMode}
          getInspections={getInspections}
          inspectionId={inspectionId}
          getFacilities={getFacilities}
          getFacility={getFacility}
        />
      )}
    </PanelList>
  );
}

function InformationCard({
  value,
  setValue,
  setMode,
  facility,
  inspections,
  setInspectionId,
  getResult,
}: any) {
  const isOutdoorEquipment = facility?.categoryId === OUTDOOR_GYM_EQUIPMENT_ID;
  const isRegularType =
    facility?.inspectionStatus === InspectionStatusList.INSPECTION_NOT_REQUIRED;
  const mdUp = useResponsive('up', 'md');

  return (
    <>
      <InspectionStatusChips status={facility?.inspectionStatus} />
      <Title sx={{ marginBottom: '4px !important' }}>
        {facility?.title || '-'}
      </Title>
      <ListSubTitleText sx={{ mb: 2 }}>
        {facility?.subCategory || ''}
      </ListSubTitleText>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        spacing={2}
      >
        {isRegularType ? (
          <Box sx={{ w: 2 }} />
        ) : (
          <Stack direction="row" alignItems="center" spacing={1}>
            <RatingStatusChips status={facility?.inspectionScore || '-'} />
            <ListContentText>
              {InspectionScoreListLabel[facility?.inspectionScore]}&nbsp;
              |&nbsp; 점검일 &nbsp;
              {fDate(facility?.inspectionDate, 'yyyy-MM-dd') || '-'}
            </ListContentText>
          </Stack>
        )}
        <ListSubContentText>
          {mdUp ? '관리번호' : ''}{' '}
          {sliceSerialNumber(facility?.serialNumber) || '-'}
        </ListSubContentText>
      </Stack>
      <Grid container mt={3}>
        <Grid item xs={6}>
          <FacilityImageInput fetch={facility?.facilityImageUrl} />
        </Grid>
        <Grid item xs={6}>
          <FacilityImageInput fetch={facility?.tagImageUrl} />
        </Grid>
        <Grid item xs={12}>
          <FacilityImageInput fetch={facility?.overviewImageUrl} />
        </Grid>
      </Grid>
      <Stack sx={{ width: '100%' }}>
        <TabContext value={value}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: 'divider',
              mt: 5,
            }}
          >
            {isOutdoorEquipment ? (
              <TabList onChange={(e, v) => setValue(v)}>
                <TabText label="시설물 정보" value="1" />
                <TabText label="점검 정보" value="2" />
              </TabList>
            ) : (
              <TabList
                onChange={(e, v) => setValue(v)}
                sx={{
                  '.MuiTabs-indicator': {
                    width: '100% !important',
                    left: '0px !important',
                  },
                  '.MuiTabs-flexContainer': {
                    justifyContent: 'center',
                  },
                }}
              >
                <TabText label="시설물 정보" value="1" />
              </TabList>
            )}
          </Box>
          {isOutdoorEquipment ? (
            <FacilityInfoPanel
              value="1"
              facility={facility}
              getResult={getResult}
            />
          ) : (
            <RegularTypeInfoPanel
              value="1"
              facility={facility}
              getResult={getResult}
            />
          )}
          <InstpectionInfoPanel
            value="2"
            setMode={setMode}
            facility={facility}
            inspections={inspections}
            setInspectionId={setInspectionId}
            getResult={getResult}
          />
        </TabContext>
      </Stack>
    </>
  );
}
